import React from 'react';
import PropTypes from 'prop-types';
import { Select, MenuItem, FormControl, InputLabel, Checkbox } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledSelectWrapper = styled(Select)(({ theme }) => ({
  backgroundColor: 'white',
  color: theme.palette.secondary.main,
  width: '100%',
  '& .MuiSelect-icon': {
    color: theme.palette.secondary.main,
  },
  '&.MuiInputBase-root': {
    height: '40px !important',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.secondary.main,
    borderWidth: 2,
  },
  '& .MuiPaper-root': {
    border: `2px solid ${theme.palette.secondary.main}`,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  color: '#404040',
  '&.Mui-selected': {
    backgroundColor: theme.palette.lighterPurple.main,
    '&:hover': {
      backgroundColor: theme.palette.lighterPurple.dark,
    },
  },
  '&:hover': {
    backgroundColor: theme.palette.lighterPurple.light,
  },
}));

const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  color: theme.palette.secondary.main,
  transform: 'translate(14px, 9px) scale(1)',
  '&.Mui-focused': {
    color: theme.palette.secondary.main,
  },
  '&.MuiInputLabel-shrink': {
    transform: 'translate(14px, -9px) scale(0.75)',
  },
}));

const StyledSelect = ({
  options,
  label,
  value,
  onChange,
  multiple = false,
  sx,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <FormControl fullWidth sx={{ minWidth: { xs: 'none', sm: 120 }, maxWidth: { xs: 'none', sm: 180 }, ...sx }}>
      <StyledInputLabel id={`${label}-select-label`}>{label}</StyledInputLabel>
      <StyledSelectWrapper
        labelId={`${label}-select-label`}
        label={label}
        multiple={multiple}
        value={value}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        onChange={(event) => onChange(event.target.value)}
        renderValue={(selected) => {
          if (multiple) {
            if (selected.includes('ALL_VALUES')) {
              return options.find((option) => option.value === 'ALL_VALUES').label;
            }
            const selectedLabels = options
              .filter((option) => selected.includes(option.value))
              .map((option) => option.label);
            return selectedLabels.join(', ');
          }
          const selectedOption = options.find((option) => option.value === selected);
          return selectedOption ? selectedOption.label : '';
        }}
        MenuProps={{
          sx: { maxHeight: '70%' },
          PaperProps: {
            sx: {
              border: (theme) => `2px solid ${theme.palette.secondary.main}`,
              borderTop: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            },
          },
        }}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderBottomLeftRadius: isOpen ? 0 : undefined,
            borderBottomRightRadius: isOpen ? 0 : undefined,
          },
        }}
      >
        {options.map((option) => (
          <StyledMenuItem key={option.value} value={option.value}>
            {multiple && (
              <Checkbox
                checked={value.includes(option.value)}
                sx={{
                  padding: 0,
                  marginRight: 1,
                  color: (theme) => theme.palette.secondary.main,
                  '&.Mui-checked': {
                    color: (theme) => theme.palette.secondary.main,
                  },
                }}
              />
            )}
            {option.label}
          </StyledMenuItem>
        ))}
      </StyledSelectWrapper>
    </FormControl>
  );
};

StyledSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  ).isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])), PropTypes.number, PropTypes.string,
  ]),
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  sx: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

StyledSelect.defaultProps = {
  value: undefined,
  multiple: false,
  sx: {},
};

export default StyledSelect;
